import React, { useState, useEffect } from 'react';
import { settingsApi, type ModelOption } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { useToast } from '../components/ui/use-toast';
import { changePassword } from '../services/auth';

const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [models, setModels] = useState<ModelOption[]>([]);

  const { toast } = useToast();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [data, modelList] = await Promise.all([
          settingsApi.getSettings(),
          settingsApi.getModels(),
        ]);
        setSettings(data || {});
        setModels(modelList || []);
      } catch (err) {
        setError('Failed to fetch settings or models');
      } finally {
        setIsLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSettings((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleRequirementChange = (index: number, value: string) => {
    const newRequirements = [...(settings.prompt_requirements || [])];
    newRequirements[index] = value;
    setSettings((prev: any) => ({ ...prev, prompt_requirements: newRequirements }));
  };

  const addRequirement = () => {
    setSettings((prev: any) => ({
      ...prev,
      prompt_requirements: [...(prev.prompt_requirements || []), ''],
    }));
  };

  const removeRequirement = (index: number) => {
    const newRequirements = [...(settings.prompt_requirements || [])];
    newRequirements.splice(index, 1);
    setSettings((prev: any) => ({ ...prev, prompt_requirements: newRequirements }));
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({ variant: 'destructive', title: 'Please fill in all password fields.' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ variant: 'destructive', title: 'Password must be at least 8 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ variant: 'destructive', title: 'Passwords do not match.' });
      return;
    }

    setUpdatingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast({ title: 'Password updated' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      toast({ variant: 'destructive', title: err?.response?.data?.detail || 'Failed to update password', description: 'Please try again.' });
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await settingsApi.updateSettings(settings);
      toast({ title: 'Settings updated' });
    } catch (err) {
      setError('Failed to update settings');
      toast({ variant: 'destructive', title: 'Failed to update settings', description: 'Please try again.' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-destructive">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold tracking-tight mb-6">Settings</h1>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Change Password</CardTitle>
          <CardDescription>
            Update your account password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
              />
            </div>
            <div className="flex justify-end">
              <Button type="submit" disabled={updatingPassword}>
                {updatingPassword ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Default Model</CardTitle>
            <CardDescription>
              Set the default generative model to be used for jobs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Label htmlFor="default_model">Model</Label>
            <select
              id="default_model"
              name="default_model"
              value={
                settings.default_model && models.some(m => m.value === settings.default_model)
                  ? settings.default_model
                  : 'gemini-2.5-flash'
              }
              onChange={(e) =>
                setSettings((prev: any) => ({ ...prev, default_model: e.target.value }))
              }
              className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {models.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label} — {m.description}
                </option>
              ))}
            </select>
            {settings.default_model && !models.some(m => m.value === settings.default_model) && (
              <p className="text-xs text-muted-foreground mt-1">
                Previously saved model is no longer available; defaulting to Gemini 2.5 Flash.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Prompt Requirements</CardTitle>
            <CardDescription>
              Define general requirements or instructions that will be applied to all jobs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {(settings.prompt_requirements || []).map((req: string, index: number) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  type="text"
                  value={req}
                  onChange={(e) => handleRequirementChange(index, e.target.value)}
                  placeholder="e.g., Output in markdown format"
                  className="flex-grow"
                />
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => removeRequirement(index)}
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={addRequirement}
              className="mt-2"
            >
              Add Requirement
            </Button>
          </CardContent>
        </Card>

        <div className="flex justify-end mt-6">
          <Button type="submit">
            Save Settings
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SettingsPage;
